import React, { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { getFindDegreesTemplate, type FindDegreesTemplate } from '../data/findDegreesTemplates';
import { DegreeLandingTemplate } from '../components/DegreeLandingTemplate';
import { usePageMeta } from '../hooks/usePageMeta';
import { buildMatchLink } from '../utils/matchParams';

/**
 * The email-capture twin of the /find-degrees/ bridge pages. Same template data
 * and same layout, but the conversion step is an email address collected in
 * place rather than a hand-off to the match flow — so the visitor never leaves
 * the page.
 */
/**
 * Pages whose whole purpose is the request itself collect full contact details
 * up front. Program-specific pages ask only for an address, since the visitor
 * came to read about the program.
 */
const CONTACT_CAPTURE_SLUGS = new Set(['request-information']);
function toEmailCapture(data: FindDegreesTemplate): FindDegreesTemplate {
  const wantsContact = CONTACT_CAPTURE_SLUGS.has(data.slug);
  return {
    ...data,
    // The request-information page carries its own labels; the program pages
    // get email-specific ones in place of their navigation CTA.
    ctaLabel: wantsContact ? data.ctaLabel : 'Send Me Program Info',
    bottomCtaLabel: wantsContact ? data.bottomCtaLabel : 'Email Me Program Info',
    heroStep2Label: wantsContact ? data.heroStep2Label : 'Confirm Email',
    heroCardSubtext: undefined,
    heroCardHelper: wantsContact ? data.heroCardHelper : 'Enter your email and we will send program details, start dates, and what each school accepts for transfer credit.',
    // Submitting hands off to the match funnel carrying the degree level, area
    // of study, and program this page is about, so nothing is asked twice.
    ctaHref: buildMatchLink('/get-matched', data.matchParams)
  };
}
export function GetInfoPage() {
  const {
    template
  } = useParams<{
    template: string;
  }>();
  const location = useLocation();
  // The explicit per-page routes carry no `:template` param, so fall back to
  // reading the slug off the path.
  const slug = template || location.pathname.split('/').filter(Boolean).pop() || '';
  const base = slug ? getFindDegreesTemplate(slug) : undefined;
  const data = base ? toEmailCapture(base) : undefined;
  usePageMeta(data?.metaTitle || 'Get Program Info | FindOnlineColleges', data?.metaDescription || 'Get details on accredited online degree programs by email.', {
    ogType: 'website'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!data) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4" data-id="element-1656" data-mp="1afa2t5op1xb2">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-1657" data-mp="1fduswca8f0ls">Page Not Found</h1>
        <p className="text-gray-600 mb-8" data-id="element-1658" data-mp="34rq7i21ud3dt">
          We couldn't find the page you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-1659" data-mp="39q1gkakbb9uz">
          Return Home
        </Link>
      </div>;
  }
  return <DegreeLandingTemplate data={data} mode="email" collect={CONTACT_CAPTURE_SLUGS.has(data.slug) ? 'contact' : 'email'} data-id="element-1660" data-mp="3ex7x8wklp0xr" />;
}